import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import authService from '../services/authService';

export enum UserRole {
  ADMIN = 'ADMIN',
  LABORATORY_MANAGER = 'LABORATORY_MANAGER',
  SAMPLE_RECEIVER = 'SAMPLE_RECEIVER',
  TECHNICIAN_PREP = 'TECHNICIAN_PREP',
  ANALYST = 'ANALYST',
  QUALITY_MANAGER = 'QUALITY_MANAGER',
  CLIENT_VIEWER = 'CLIENT_VIEWER',
  
  // Laboratory-specific roles for SAA
  SAA_PREP_CHIMIQUE = 'SAA_PREP_CHIMIQUE',
  SAA_ANALYST = 'SAA_ANALYST',
  
  // Laboratory-specific roles for ICP
  ICP_PREP_CHIMIQUE = 'ICP_PREP_CHIMIQUE',
  ICP_ANALYST = 'ICP_ANALYST',
  
  // Laboratory-specific roles for AU
  AU_PREP_CHIMIQUE = 'AU_PREP_CHIMIQUE',
  AU_ANALYST = 'AU_ANALYST',
  
  // Laboratory-specific roles for Volumétrie
  VOLUMETRIE_PREP_CHIMIQUE = 'VOLUMETRIE_PREP_CHIMIQUE',
  VOLUMETRIE_ANALYST = 'VOLUMETRIE_ANALYST',
  
  // Laboratory-specific roles for Physico-Chimique (no prep chimique)
  PHYSICO_CHIMIQUE_ANALYST = 'PHYSICO_CHIMIQUE_ANALYST',
  
  // Mechanical preparation (shared across all laboratories)
  PREPARATION_MECANIQUE = 'PREPARATION_MECANIQUE',
  
  // Validation service
  VALIDATION_SERVICE = 'VALIDATION_SERVICE',
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  department: string;
  employeeCode: string;
  avatar: string | null;
  permissions: string[];
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string; refreshToken: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'REFRESH_TOKEN'; payload: { token: string } }
  | { type: 'UPDATE_USER'; payload: User };

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('access_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  isLoading: false,
  isAuthenticated: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        isLoading: false,
        isAuthenticated: true,
      };
    
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isLoading: false,
        isAuthenticated: false,
      };
    
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
      };
    
    case 'REFRESH_TOKEN':
      return {
        ...state,
        token: action.payload.token,
      };
    
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    
    default:
      return state;
  }
}

interface AuthContextType extends Omit<AuthState, 'refreshToken'> {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: UserRole | UserRole[]) => boolean;
  canAccessSample: (sampleStatus: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from token
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const decoded = jwtDecode<any>(token);
        if (decoded.exp * 1000 > Date.now()) {
          // Token is valid, fetch user data
          fetchUserProfile();
        } else {
          // Token expired
          dispatch({ type: 'LOGOUT' });
        }
      } catch (error) {
        dispatch({ type: 'LOGOUT' });
      }
    }
  }, []);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('access_token') || '';

      
      const user = await authService.getProfile();
      const refreshToken = localStorage.getItem('refresh_token') || '';
      
      // Store user data in localStorage for fallback access
      localStorage.setItem('user', JSON.stringify(user));
      

      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: user as User,
          token,
          refreshToken,
        },
      });
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      dispatch({ type: 'LOGOUT' });
    }
  };

  const login = async (email: string, password: string) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const response = await authService.login({ email, password });
      
      // Store tokens in localStorage
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      // Store user data in localStorage for fallback access
      localStorage.setItem('user', JSON.stringify(response.user));
      

      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.user as User,
          token: response.access_token,
          refreshToken: response.refresh_token,
        },
      });
    } catch (error) {
      console.error('❌ AuthContext: Login failed:', error);
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    
    dispatch({ type: 'LOGOUT' });
  };

  const refreshTokenFn = async () => {
    try {
      const response = await authService.refreshToken();
      dispatch({
        type: 'REFRESH_TOKEN',
        payload: { token: response.access_token },
      });
    } catch (error) {
      console.error('Token refresh failed:', error);
      dispatch({ type: 'LOGOUT' });
      throw error;
    }
  };

  const hasPermission = (permission: string): boolean => {
    return state.user?.permissions.includes(permission) || state.user?.permissions.includes('*') || false;
  };

  const hasRole = (role: UserRole | UserRole[]): boolean => {
    if (!state.user) return false;
    const roles = Array.isArray(role) ? role : [role];
    return roles.includes(state.user.role);
  };

  const canAccessSample = (sampleStatus: string): boolean => {
    if (!state.user) return false;
    
    // Admin can access everything
    if (state.user.role === UserRole.ADMIN) return true;
    
    // Role-based access control
    switch (sampleStatus) {
      case 'RECEPTION':
        return hasRole([UserRole.SAMPLE_RECEIVER, UserRole.LABORATORY_MANAGER]);
      case 'PREPARATION_MECANIQUE':
        return hasRole([UserRole.TECHNICIAN_PREP, UserRole.LABORATORY_MANAGER]);
      case 'ANALYSE_CHIMIQUE':
        return hasRole([UserRole.ANALYST, UserRole.LABORATORY_MANAGER]);
      case 'PRET':
      case 'EN_ATTENTE':
        return hasRole([UserRole.LABORATORY_MANAGER, UserRole.QUALITY_MANAGER]);
      default:
        return false;
    }
  };

  const value: AuthContextType = {
    user: state.user,
    token: state.token,
    isLoading: state.isLoading,
    isAuthenticated: state.isAuthenticated,
    login,
    logout,
    refreshToken: refreshTokenFn,
    hasPermission,
    hasRole,
    canAccessSample,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 